import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
}, (error) => Promise.reject(error));

/* 401 VUOL DIRE UNA COSA SOLA: la sessione non vale piu'. Si chiude e
   si torna all'accesso, senza eccezioni.
   Fino al 21 settembre 2026 qui c'erano due liste di rotte «dove il 401
   non e' una sessione» - login, recupero password, verifica del secondo
   fattore - perche' il server rispondeva 401 anche a una password o a
   un codice sbagliati. Le liste di eccezioni crescono, e la rotta che
   nessuno aggiunge sloga l'utente a caso. Adesso e' il server a non
   rispondere 401 per quei motivi: risponde 422 con un `code`. Se un
   giorno una rotta nuova torna a farlo, il sintomo e' un logout
   inspiegabile, e la correzione sta nel server, non in una lista qui. */
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        // Il backend blocca tutte le route finche' la password provvisoria
        // non viene sostituita: qui riportiamo l'utente sulla schermata giusta.
        if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_DA_CAMBIARE') {
            if (window.location.pathname !== '/cambia-password') {
                window.location.href = '/cambia-password';
            }
        }
        // Stesso schema per l'obbligo del secondo fattore a periodo di
        // grazia finito: il server lascia aperte solo le rotte
        // dell'attivazione, e qui si porta l'utente dove puo' farla.
        if (error.response?.status === 403 && error.response?.data?.code === 'SERVE_ATTIVARE_2FA') {
            if (window.location.pathname !== '/profilo') {
                window.location.href = '/profilo';
            }
        }

        // Questo interceptor rigetta il BODY della risposta, non l'oggetto
        // errore di axios. Alcuni componenti pero' leggono il messaggio come
        // e.response.data.message (la forma di axios): senza questo innesto
        // troverebbero undefined e mostrerebbero un messaggio generico al
        // posto di quello del backend.
        //
        // La proprieta' e' non enumerabile: non compare negli spread ne'
        // in JSON.stringify, e la copia evita riferimenti circolari.
        const corpo = error.response?.data || { success: false, message: 'Errore di connessione' };
        if (corpo && typeof corpo === 'object' && !corpo.response) {
            Object.defineProperty(corpo, 'response', {
                value: { status: error.response?.status, data: { ...corpo } },
                enumerable: false,
                configurable: true,
            });
        }
        return Promise.reject(corpo);
    }
);

export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    getProfile: () => api.get('/auth/me'),
    cambiaPassword: (data) => api.post('/auth/cambia-password', data),
    passwordDimenticata: (data) => api.post('/auth/password-dimenticata', data),
    reimpostaPassword: (data) => api.post('/auth/reimposta-password', data),
};

/* La verifica in due passaggi.
   `verifica` e' l'unica che si chiama senza sessione: porta il gettone
   provvisorio nel corpo, non nell'intestazione, perche' una sessione
   ancora non c'e'. */
export const secondoFattoreAPI = {
    stato: () => api.get('/auth/2fa/stato'),
    avvia: () => api.post('/auth/2fa/avvia'),
    conferma: (codice) => api.post('/auth/2fa/conferma', { codice }),
    verifica: (dati) => api.post('/auth/2fa/verifica', dati),
    rigeneraCodici: (password, codice) => api.post('/auth/2fa/codici', { password, codice }),
    disattiva: (password, codice) => api.post('/auth/2fa/disattiva', { password, codice }),
};

export const pazientiAPI = {
    getAll: (params) => api.get('/pazienti', { params }),
    getById: (id) => api.get(`/pazienti/${id}`),
    create: (data) => api.post('/pazienti', data),
    update: (id, data) => api.put(`/pazienti/${id}`, data),
    updateNote: (id, note) => api.patch(`/pazienti/${id}/note`, { note }),
    /* Due campi distinti, e non e' un dettaglio: il medico curante e'
       UNO e lo dichiara il paziente in accettazione; gli altri
       professionisti sono un testo libero. Nessuno dei due e' la tabella
       `medici`, che e' l'elenco dei collaboratori della clinica. */
    updateAltriProfessionisti: (id, testo) =>
        api.patch(`/pazienti/${id}/altri-professionisti`, { altri_professionisti: testo }),
    updateMedicoCurante: (id, dati) =>
        api.patch(`/pazienti/${id}/medico-curante`, dati),
    delete: (id) => api.delete(`/pazienti/${id}`),
    exportCSV: () => api.get('/pazienti/export/csv', { responseType: 'blob' }),
    exportVCard: () => api.get('/pazienti/export/vcard', { responseType: 'blob' }),
};

export default api;

export const anamnesiAPI = {
    getByPazienteId: (pazienteId) => api.get(`/anamnesi/${pazienteId}`),
    createOrUpdate: (pazienteId, data) => api.post(`/anamnesi/${pazienteId}`, data),
    getAnnotazioni: (pazienteId, tipo) => api.get(`/anamnesi/${pazienteId}/annotazioni`, { params: { tipo } }),
    createAnnotazione: (pazienteId, data) => api.post(`/anamnesi/${pazienteId}/annotazioni`, data),
    deleteAnnotazione: (id) => api.delete(`/anamnesi/annotazioni/${id}`),
};

export const visiteAPI = {
    getByPazienteId: (pazienteId) => api.get(`/visite/paziente/${pazienteId}`),
    getById: (id) => api.get(`/visite/${id}`),
    create: (pazienteId, data) => api.post(`/visite/paziente/${pazienteId}`, data),
    /* Fase 2 del riordino: una procedura in piu' nella visita di oggi, e la
       spunta «Includi nel referto» del suo riquadro. */
    aggiungiPrestazione: (visitaId, data) => api.post(`/visite/${visitaId}/prestazioni`, data),
    includiNelReferto: (visitaId, rigaId, includi) =>
        api.patch(`/visite/${visitaId}/prestazioni/${rigaId}/referto`, { includi }),
    update: (id, data) => api.put(`/visite/${id}`, data),
    delete: (id) => api.delete(`/visite/${id}`),
    /* `dati` porta { urgenza, motivo } quando si apre senza
       autorizzazione dichiarando un'urgenza clinica. Senza, apre solo
       chi e' autorizzato e la risposta e' 403 per gli altri. */
    apriRiservata: (id, dati) => api.post(`/visite/${id}/apri-riservata`, dati || {}),
    aperture: () => api.get('/visite/aperture'),
    /* «Le ho viste»: scrive `notificato_il`, la colonna che dal 2026
       dichiarava che il medico esecutore era stato avvisato e che
       nessuno scriveva. */
    apertureViste: () => api.post('/visite/aperture/viste'),

    // Autorizzazioni alle procedure riservate
    richiediAccesso: (id, motivo) =>
        api.post(`/visite/${id}/richiedi-accesso`, { motivo }),
    richiesteAccesso: () => api.get('/visite/richieste-accesso'),
    decidiRichiesta: (id, concessa, note) =>
        api.post(`/visite/richieste-accesso/${id}/decidi`, { concessa, note }),

    // Moduli
    getModuli: (visitaId) => api.get(`/visite/${visitaId}/moduli`),
    createModulo: (visitaId, data) => api.post(`/visite/${visitaId}/moduli`, data),
    updateModulo: (id, data) => api.put(`/visite/moduli/${id}`, data),
    deleteModulo: (id) => api.delete(`/visite/moduli/${id}`),
};

export const mediciAPI = {
    getAll: (soloAttivi = false) => api.get(`/medici${soloAttivi ? '?soloAttivi=true' : ''}`),
    getById: (id) => api.get(`/medici/${id}`),
    create: (data) => api.post('/medici', data),
    update: (id, data) => api.put(`/medici/${id}`, data),
    delete: (id) => api.delete(`/medici/${id}`),
    getScadenze: () => api.get('/medici/scadenze-assicurazioni'),
    riferimenti: (id) => api.get(`/medici/${id}/riferimenti`),
};

export const prestazioniAPI = {
    getAll: (soloAttive = false) => api.get(`/prestazioni${soloAttive ? '?soloAttive=true' : ''}`),
    getById: (id) => api.get(`/prestazioni/${id}`),
    getAliquote: () => api.get('/prestazioni/aliquote'),
    getTipiSpesa: () => api.get('/prestazioni/tipi-spesa'),
    getMacroAree: () => api.get('/prestazioni/macro-aree'),
    create: (data) => api.post('/prestazioni', data),
    update: (id, data) => api.put(`/prestazioni/${id}`, data),
    delete: (id) => api.delete(`/prestazioni/${id}`),
};

export const spettanzeAPI = {
    getRiepilogo: (params = {}) => api.get('/spettanze/riepilogo', { params }),
    getDettaglio: (params = {}) => api.get('/spettanze/dettaglio', { params }),
};

export const anamnesiFisiologicaAPI = {
    getDettaglio: (pazienteId) => api.get(`/anamnesi/${pazienteId}/fisiologica-dettaglio`),
    /* `origine` dice da dove si scrive il riquadro delle gravidanze
       (anamnesi o scheda ginecologica): lo registra il database accanto
       all'autore dell'ultima modifica. */
    saveDettaglio: (pazienteId, data, params) =>
        api.post(`/anamnesi/${pazienteId}/fisiologica-dettaglio`, data, params ? { params } : undefined),
    /* Il riquadro delle gravidanze al giorno di una visita: { compilazione }
       oppure { visita }. Risponde se e' modificabile (oggi) e il valore. */
    getGravidanzeAl: (pazienteId, params) =>
        api.get(`/anamnesi/${pazienteId}/gravidanze-al`, { params }),
};

export const procedureAPI = {
    getRecap: (pazienteId) => api.get(`/procedure/paziente/${pazienteId}`),
    getDettaglio: (pazienteId, nome) => api.get(`/procedure/paziente/${pazienteId}/dettaglio`, { params: { nome } }),
};

export const alertAPI = {
    getByPaziente: (pazienteId) => api.get(`/alert/paziente/${pazienteId}`),
    create: (pazienteId, data) => api.post(`/alert/paziente/${pazienteId}`, data),
    update: (id, data) => api.put(`/alert/${id}`, data),
    remove: (id) => api.delete(`/alert/${id}`),
    getDue: () => api.get('/alert/due'),
    markNotificato: (id) => api.put(`/alert/${id}/notificato`),
};

export const fotoAPI = {
    getByPaziente: (pazienteId) => api.get(`/foto/paziente/${pazienteId}`),
    // Content-Type undefined -> il browser imposta multipart/form-data col boundary corretto
    upload: (pazienteId, formData) => api.post(`/foto/paziente/${pazienteId}`, formData, {
        headers: { 'Content-Type': undefined },
    }),
    remove: (id) => api.delete(`/foto/${id}`),
};

export const rxAPI = {
    // L'abilitazione sta sul medico: la schermata chiede al server se deve
    // mostrare il tasto. Non e' la protezione - quella e' su ogni rotta -
    // e' il non mettere in menu una cosa che poi risponderebbe 403.
    accesso: () => api.get('/rx/accesso'),
    perPaziente: (pazienteId) => api.get(`/rx/paziente/${pazienteId}`),
    crea: (pazienteId, data) => api.post(`/rx/paziente/${pazienteId}`, data),
    modifica: (id, data) => api.put(`/rx/${id}`, data),
    elimina: (id) => api.delete(`/rx/${id}`),
    elenco: (params) => api.get('/rx', { params }),
    mediciAbilitati: () => api.get('/rx/medici'),
    esporta: (params) => api.get('/rx/esporta', { params, responseType: 'blob' }),
};

export const campiAPI = {
    aree: () => api.get('/campi/aree'),
    scale: () => api.get('/campi/scale'),
    elenco: (params) => api.get('/campi', { params }),
    crea: (data) => api.post('/campi', data),
    modifica: (id, data) => api.put(`/campi/${id}`, data),
    ritira: (id) => api.post(`/campi/${id}/ritira`),
    riattiva: (id) => api.post(`/campi/${id}/riattiva`),
    proposte: () => api.get('/campi/proposte'),
    proponi: (data) => api.post('/campi/proposte', data),
    decidi: (id, data) => api.post(`/campi/proposte/${id}/decidi`, data),
};

export const schedeAPI = {
    elenco: () => api.get('/schede'),
    dettaglio: (id) => api.get(`/schede/${id}`),
    crea: (data) => api.post('/schede', data),
    modifica: (id, data) => api.put(`/schede/${id}`, data),
    // Attivare, disattivare, ordinare, assegnare a un'area: e' quello che
    // il server concede anche alla segreteria. Tutto il resto no.
    anagrafica: (id, data) => api.patch(`/schede/${id}/anagrafica`, data),
    creaSezione: (id, data) => api.post(`/schede/${id}/sezioni`, data),
    modificaSezione: (id, data) => api.put(`/schede/sezioni/${id}`, data),
    eliminaSezione: (id) => api.delete(`/schede/sezioni/${id}`),
    aggiungiCampo: (id, data) => api.post(`/schede/${id}/campi`, data),
    modificaCampo: (id, data) => api.put(`/schede/campi/${id}`, data),
    togliCampo: (id) => api.delete(`/schede/campi/${id}`),
    mie: () => api.get('/schede/mie'),
    creaVariante: (id, data) => api.post(`/schede/${id}/variante`, data),
    variante: (id) => api.get(`/schede/variante/${id}`),
    differenza: (id, data) => api.post(`/schede/variante/${id}/differenze`, data),
    togliDifferenza: (id, diffId) => api.delete(`/schede/variante/${id}/differenze/${diffId}`),
    eliminaVariante: (id) => api.delete(`/schede/variante/${id}`),
};

export const dettaglioAPI = {
    get: (visitaId) => api.get(`/visita-dettaglio/visita/${visitaId}`),
    saveCampo: (visitaId, campo, valore) => api.put(`/visita-dettaglio/visita/${visitaId}`, { campo, valore }),
};

export const utentiAPI = {
    getAll: () => api.get('/utenti'),
    create: (data) => api.post('/utenti', data),
    setStato: (id, attivo) => api.patch(`/utenti/${id}/stato`, { attivo }),
    resetPassword: (id, password) => api.patch(`/utenti/${id}/password`, { password }),
    azzera2fa: (id) => api.post(`/utenti/${id}/azzera-2fa`),
    reinviaCredenziali: (id) => api.post(`/utenti/${id}/reinvia-credenziali`),
};

export const cestinoAPI = {
    getAll: () => api.get('/cestino'),
    ripristina: (id) => api.post(`/cestino/${id}/ripristina`),
    elimina: (id, conferma) => api.delete(`/cestino/${id}`, { data: { conferma } }),
};

export const anamnesiVociAPI = {
    getByPaziente: (pazienteId, storico = false) =>
        api.get(`/anamnesi-voci/paziente/${pazienteId}`, { params: { storico } }),
    crea: (pazienteId, data) => api.post(`/anamnesi-voci/paziente/${pazienteId}`, data),
    aggiorna: (id, data) => api.put(`/anamnesi-voci/${id}`, data),
    supera: (id, note) => api.post(`/anamnesi-voci/${id}/supera`, { note }),
    riattiva: (id) => api.post(`/anamnesi-voci/${id}/riattiva`),
    cancella: (id) => api.delete(`/anamnesi-voci/${id}`),
};

export const auditAPI = {
    accessi: (params = {}) => api.get('/audit/accessi', { params }),
    riepilogo: () => api.get('/audit/riepilogo'),
    modifiche: (params = {}) => api.get('/audit/modifiche', { params }),
    ripristinaModifica: (id) => api.post(`/audit/modifiche/${id}/ripristina`),
};

export const auditModificheAPI = {
    lista: (params = {}) => api.get('/audit/modifiche', { params }),
};

Object.assign(alertAPI, {
    lista: (params = {}) => api.get('/alert', { params }),
    crea: (data) => api.post('/alert', data),
    completa: (id) => api.post(`/alert/${id}/completa`),
    riapri: (id) => api.post(`/alert/${id}/riapri`),
    segnaLetto: (id) => api.post(`/alert/${id}/letto`),
    rinvia: (id, minuti) => api.post(`/alert/${id}/rinvia`, { minuti }),
});

/* Le liste chiuse dell'iscrizione all'albo (migration 190). */
export const albiAPI = {
    elenco: () => api.get('/albi'),
    aggiungiSede: (codice, dati) => api.post(`/albi/${codice}/sedi`, dati),
    aggiornaSede: (id, dati) => api.put(`/albi/sedi/${id}`, dati),
};
